import { computed, ref, toRef, useHost, onMounted, type Ref, type ComputedRef, type ToRefs } from "vue";
import type { InputProps } from "./input.types";
import type { AcceptableValue } from "@/runtime/types/utils";
import type { Colors, Variants, Sizes } from "types";
import { useSharedState } from "composables/useSharedState";
import { useFormAssociated, type FormValue } from "composables/useFormAssociated";
import { useIcons } from "composables/useIcons";
import { useTagReorder, type ReorderTagBindings, type ReorderContainerBindings } from "composables/useTagReorder";
import { useFieldInheritance } from "components/Field/useField";
import { looseToNumber } from "utils";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { inputClasses } from "./input.styles";
import { cardClasses } from "components/Card/card.styles";

import { createEventDispatcher } from "@/runtime/shared/events";

/**
 * Sets up autofocus on an element with optional delay.
 * Shared utility for all input-related composables.
 *
 * @param element - Element or ref to focus
 * @param delay - Delay in milliseconds (default 0)
 */
export const setupAutofocus = (
  element: HTMLElement | HTMLInputElement | Ref<HTMLInputElement | null> | null,
  delay: number = 0,
) => {
  const el = "value" in (element || {}) ? (element as Ref).value : element;
  if (el) {
    setTimeout(() => {
      el.focus();
    }, delay);
  }
};

/**
 * Traverses shadow DOM to get native input element from e-input custom element.
 * Shared utility for components that wrap e-input (like InputNumber, InputOtp).
 *
 * @param host - The host element containing e-input
 * @param selector - Optional selector for e-input (default 'e-input')
 * @returns Native HTMLInputElement or null
 */
export const getNestedInput = (host: Element | null, selector: string = "e-input"): HTMLInputElement | null => {
  if (!host) return null;

  const shadowRoot = (host as HTMLElement).shadowRoot;
  if (!shadowRoot) return null;

  const inputElement = shadowRoot.querySelector(selector);
  if (!inputElement) return null;

  return inputElement.shadowRoot?.querySelector("input") || null;
};

/**
 * Gets all nested input elements from multiple e-input custom elements.
 * Used by components that render multiple inputs (like InputOtp).
 *
 * @param host - The host element containing e-input elements
 * @param selector - Optional selector for e-input (default 'e-input')
 * @returns Array of native HTMLInputElement
 */
export const getNestedInputs = (host: Element | null, selector: string = "e-input"): HTMLInputElement[] => {
  if (!host?.shadowRoot) return [];

  const eInputs = host.shadowRoot.querySelectorAll(selector);
  const inputs: HTMLInputElement[] = [];

  for (const eInput of eInputs) {
    if (eInput.shadowRoot) {
      const input = eInput.shadowRoot.querySelector("input");
      if (input) {
        inputs.push(input);
      }
    }
  }

  return inputs;
};

/** Union type for model value (single, array, or unset). */
type InputModelValue = AcceptableValue | AcceptableValue[] | undefined;

/** Shared context for module-level input event handlers. */
interface InputContext {
  props: InputProps;
  isMultiple: ComputedRef<boolean | undefined>;
  inputValue: Ref<string>;
  inputRef: Ref<HTMLInputElement | null>;
  tags: ComputedRef<AcceptableValue[]>;
  addTag: (value: string) => boolean;
  setModelValue: (value: InputModelValue) => void;
  processValue: (value: string) => AcceptableValue;
  validate: () => void;
  dispatchEvent: ReturnType<typeof createEventDispatcher>;
}

/** Apply trim modifier. */
const applyTrim = (value: AcceptableValue): AcceptableValue => {
  return typeof value === "string" ? value.trim() || null : value;
};

/** Apply number modifier. */
const applyNumber = (value: AcceptableValue): AcceptableValue => {
  return looseToNumber(value);
};

/** Process value applying model modifiers (trim, number, nullify). */
const processValue = (props: InputProps, value: string): AcceptableValue => {
  const modifiers = props.modelModifiers;
  let processed: AcceptableValue = value;

  if (modifiers?.trim) processed = applyTrim(processed);
  if (modifiers?.number || props.type === "number") processed = applyNumber(processed);
  if (modifiers?.nullify) processed = processed || null;

  return processed;
};

/** Handle delimiter splitting for multiple-mode input. */
const handleMultipleInput = (ctx: InputContext, value: string, target: HTMLInputElement): boolean => {
  if (ctx.props.delimiter && value.includes(ctx.props.delimiter)) {
    const parts = value.split(ctx.props.delimiter);
    for (let i = 0; i < parts.length - 1; i++) {
      ctx.addTag(parts[i]);
    }
    ctx.inputValue.value = parts.at(-1) ?? "";
    target.value = ctx.inputValue.value;
    return true;
  }
  ctx.inputValue.value = value;
  return false;
};

/** Handle the input event. */
const handleInput = (ctx: InputContext, e: Event): void => {
  const value = (e.target as HTMLInputElement).value;

  if (ctx.isMultiple.value) {
    handleMultipleInput(ctx, value, e.target as HTMLInputElement);
  } else {
    const modifiers = ctx.props.modelModifiers;
    if (!modifiers?.lazy) {
      ctx.setModelValue(ctx.processValue(value));
      ctx.validate();
    }
  }
};

/** Handle Enter key in multiple mode: add tag. */
const handleEnterMultiple = (ctx: InputContext, target: HTMLInputElement, e: KeyboardEvent): void => {
  e.preventDefault();
  e.stopPropagation();
  if (ctx.addTag(target.value)) {
    ctx.inputValue.value = "";
    target.value = "";
  }
};

/** Handle Enter key in single mode: submit form. */
const handleEnterSingle = (target: HTMLInputElement, e: KeyboardEvent): void => {
  e.preventDefault();
  const form = target.closest("form") || (target.getRootNode() as ShadowRoot)?.host?.closest("form");
  if (form) form.requestSubmit();
};

/** Handle the keydown event. */
const handleKeydown = (ctx: InputContext, e: KeyboardEvent): void => {
  const target = e.target as HTMLInputElement;
  const currentValue = target.value;

  if (e.key === "Enter") {
    if (ctx.isMultiple.value && currentValue.trim()) {
      handleEnterMultiple(ctx, target, e);
    } else {
      handleEnterSingle(target, e);
    }
    return;
  }

  if (e.key === "Backspace" && ctx.isMultiple.value && !currentValue) {
    const currentTags = ctx.tags.value;
    if (currentTags.length > 0) {
      ctx.setModelValue(currentTags.filter((v) => v !== currentTags.at(-1)));
    }
  }
};

/** Handle the change event. */
const handleChange = (ctx: InputContext, e: Event): void => {
  const value = (e.target as HTMLInputElement).value;
  const modifiers = ctx.props.modelModifiers;

  if (!ctx.isMultiple.value) {
    if (modifiers?.lazy) ctx.setModelValue(ctx.processValue(value));
    if (modifiers?.trim) (e.target as HTMLInputElement).value = value.trim();
  }

  ctx.validate();
  ctx.dispatchEvent("change", true);
};

/** Handle the blur event. */
const handleBlur = (ctx: InputContext): void => {
  if (ctx.isMultiple.value && ctx.inputValue.value.trim() && ctx.addTag(ctx.inputValue.value)) {
    ctx.inputValue.value = "";
    if (ctx.inputRef.value) ctx.inputRef.value.value = "";
  }

  ctx.validate();
  ctx.dispatchEvent("blur", true);
};

/** Convert value to form-associated value. */
const toFormValue = (value: unknown, name: string): FormValue => {
  if (value === undefined || value === null) return null;

  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    const formData = new FormData();
    for (const v of value) formData.append(name, String(v));
    return formData;
  }

  return String(value);
};

export interface UseInputReturn {
  // State refs
  state: ToRefs<InputProps>;

  // Host element
  host: Element | null;

  // Field inheritance
  finalColor: ComputedRef<Colors>;
  finalVariant: ComputedRef<Exclude<Variants, "link">>;
  finalSize: ComputedRef<Sizes>;

  // Classes (computed in composable)
  classes: ComputedRef<{ base: string; icon: string; input: string; placeholder: string }>;

  // Drag-reorder bindings (multiple mode)
  tagBindings: (index: number) => ReorderTagBindings;
  reorderContainerBindings: () => ReorderContainerBindings;

  // Multiple mode
  isMultiple: ComputedRef<boolean | undefined>;

  // Internal input value (for multiple mode)
  inputValue: Ref<string>;

  // Tags (multiple mode)
  tags: ComputedRef<AcceptableValue[]>;
  addTag: (value: string) => boolean;
  removeTag: (value: AcceptableValue) => void;
  getDisplayValue: (value: AcceptableValue) => string;

  // Value management
  setModelValue: (value: InputModelValue) => void;
  processValue: (value: string) => AcceptableValue;

  // Single mode value
  singleValue: ComputedRef<AcceptableValue | AcceptableValue[]>;

  // Icons
  isLeading: ComputedRef<boolean | string | undefined>;
  isTrailing: ComputedRef<boolean | string | undefined>;
  leadingIconName: ComputedRef<string | undefined>;
  trailingIconName: ComputedRef<string | undefined>;

  // Form
  inputRef: Ref<HTMLInputElement | null>;
  validate: () => void;

  // Event handlers
  handleInput: (e: Event) => void;
  handleKeydown: (e: KeyboardEvent) => void;
  handleChange: (e: Event) => void;
  handleBlur: () => void;
}

/** Build tag management functions for multiple-mode input. */
const buildTagManagement = (
  props: InputProps,
  isMultiple: ComputedRef<boolean | undefined>,
  tags: ComputedRef<AcceptableValue[]>,
  setModelValue: (value: InputModelValue) => void,
) => {
  const addTag = (value: string): boolean => {
    if (!isMultiple.value || props.disabled) return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    const convertedValue = props.convertValue ? props.convertValue(trimmed) : trimmed;
    const currentTags = tags.value;
    if (props.max !== undefined && currentTags.length >= props.max) return false;
    if (!props.duplicate && currentTags.includes(convertedValue)) return false;
    setModelValue([...currentTags, convertedValue]);
    return true;
  };
  const removeTag = (value: AcceptableValue) => {
    if (!isMultiple.value || props.disabled) return;
    setModelValue(tags.value.filter((v) => v !== value));
  };
  return { addTag, removeTag };
};

/** Build form-associated validation and tag/value management. */
const buildInputFormAndValues = (
  props: InputProps,
  state: ToRefs<InputProps>,
  dispatchEvent: ReturnType<typeof createEventDispatcher>,
) => {
  const inputRef = ref<HTMLInputElement | null>(null);
  const inputValue = ref("");
  const isMultiple = computed(() => state.multiple?.value ?? props.multiple);
  const { validate } = useFormAssociated({
    inputRef,
    modelValue: state.modelValue as any,
    disabled: toRef(props, "disabled"),
    required: toRef(props, "required"),
    toFormValue: (value) => toFormValue(value, state.name?.value ?? ""),
    disableEnterSubmit: true,
  });
  const tags = computed(() => {
    if (!isMultiple.value) return [];
    const value = state.modelValue?.value;
    return Array.isArray(value) ? value : [];
  });
  const setModelValue = (value: InputModelValue) => dispatchEvent("update:modelValue", value);
  const { addTag, removeTag } = buildTagManagement(props, isMultiple, tags, setModelValue);
  const getDisplayValue = (value: AcceptableValue): string =>
    props.displayValue ? props.displayValue(value) : String(value);
  const singleValue = computed(() => (isMultiple.value ? "" : (state.modelValue?.value ?? "")));
  return {
    inputRef,
    inputValue,
    isMultiple,
    validate,
    tags,
    setModelValue,
    addTag,
    removeTag,
    getDisplayValue,
    singleValue,
  };
};

interface BuildClassesArgs {
  props: InputProps;
  state: ReturnType<typeof useSharedState<InputProps>>;
  finalColor: ComputedRef<InputProps["color"]>;
  finalVariant: ComputedRef<InputProps["variant"]>;
  finalSize: ComputedRef<InputProps["size"]>;
  isMultiple: ComputedRef<boolean | undefined>;
}

/** Compose card + variant + mono-focus class refs into the per-slot class bag. */
const buildInputClasses = ({ props, state, finalColor, finalVariant, finalSize, isMultiple }: BuildClassesArgs) => {
  const variantClasses = computed(() =>
    inputClasses({
      variant: finalVariant.value,
      size: finalSize.value,
      disabled: state.disabled?.value,
      multiple: isMultiple.value,
      block: state.block?.value,
      square: props.square,
    }),
  );

  const monoFocusClass = computed(() => {
    if (finalColor.value === "mono" && (finalVariant.value === "solid" || finalVariant.value === "outline")) {
      return "focus-within:ring-0! focus-within:outline-1 focus-within:outline-solid focus-within:outline-(--color-foreground) focus-within:outline-offset-4";
    }
    return "";
  });

  const cardCls = computed(() =>
    cardClasses({ color: finalColor.value, variant: finalVariant.value, size: finalSize.value }),
  );

  return computed(() => ({
    base: mergeClassProp(`${cardCls.value} ${variantClasses.value.base} ${monoFocusClass.value}`.trim(), props.class),
    icon: variantClasses.value.icon,
    input: variantClasses.value.input,
    placeholder: variantClasses.value.placeholder,
  }));
};

export const useInput = (props: InputProps): UseInputReturn => {
  const state = useSharedState(props);
  const host = useHost();
  const dispatchEvent = createEventDispatcher(host);
  const { finalColor, finalVariant, finalSize } = useFieldInheritance(host, state);
  const { isLeading, isTrailing, leadingIconName, trailingIconName } = useIcons(state);
  const fv = buildInputFormAndValues(props, state, dispatchEvent);
  const ctx: InputContext = {
    props,
    isMultiple: fv.isMultiple,
    inputValue: fv.inputValue,
    inputRef: fv.inputRef,
    tags: fv.tags,
    addTag: fv.addTag,
    setModelValue: fv.setModelValue,
    processValue: (value: string) => processValue(props, value),
    validate: fv.validate,
    dispatchEvent,
  };

  const classes = buildInputClasses({ props, state, finalColor, finalVariant, finalSize, isMultiple: fv.isMultiple });

  const { tagBindings, containerBindings: reorderContainerBindings } = useTagReorder({
    sortable: toRef(props, "sortable"),
    tags: fv.tags,
    onReorder: (next) => fv.setModelValue(next),
  });

  onMounted(() => {
    if (props.autofocus) setupAutofocus(fv.inputRef, props.autofocusDelay ?? 0);
  });
  if (host) (host as any).focus = () => fv.inputRef.value?.focus();

  return {
    state,
    host,
    finalColor,
    finalVariant,
    finalSize,
    classes,
    tagBindings,
    reorderContainerBindings,
    ...fv,
    processValue: (value: string) => processValue(props, value),
    isLeading,
    isTrailing,
    leadingIconName,
    trailingIconName,
    handleInput: (e: Event) => handleInput(ctx, e),
    handleKeydown: (e: KeyboardEvent) => handleKeydown(ctx, e),
    handleChange: (e: Event) => handleChange(ctx, e),
    handleBlur: () => handleBlur(ctx),
  };
};
